import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createAsyncStorage } from '@/lib/asyncStorage'
import { chromeStorage } from '@/lib/storage'

export interface FinanceItem {
  id: string
  title: string
  amount: number
  type: 'income' | 'expense'
  category: string
  date: string
  createdAt: number
}

export interface FinanceState {
  items: FinanceItem[]
  addItem: (item: Omit<FinanceItem, 'id' | 'createdAt'> & { id?: string }) => void
  deleteItem: (id: string) => void
  getTotalIncome: () => number
  getTotalExpense: () => number
  getBalance: () => number
  getItemsByMonth: (year: number, month: number) => FinanceItem[]
  clearAllItems: () => void
}

export const useFinanceStore = create<FinanceState>()(
  persist(
    (set, get) => ({
      items: [],
      addItem: (itemData) =>
        set((state) => {
          const item: FinanceItem = {
            ...(itemData as Omit<FinanceItem, 'id' | 'createdAt'>),
            id: itemData.id || `finance-${Date.now()}`,
            createdAt: Date.now(),
          }
          const currentItems = Array.isArray(state.items) ? state.items : []
          return { items: [...currentItems, item] }
        }),
      deleteItem: (id) =>
        set((state) => {
          const currentItems = Array.isArray(state.items) ? state.items : []
          return {
            items: currentItems.filter((i) => i.id !== id),
          }
        }),
      getTotalIncome: () => {
        const items = get().items
        return Array.isArray(items)
          ? items.filter((i) => i?.type === 'income').reduce((sum, i) => sum + i.amount, 0)
          : 0
      },
      getTotalExpense: () => {
        const items = get().items
        return Array.isArray(items)
          ? items.filter((i) => i?.type === 'expense').reduce((sum, i) => sum + i.amount, 0)
          : 0
      },
      getBalance: () => {
        const state = get()
        return state.getTotalIncome() - state.getTotalExpense()
      },
      getItemsByMonth: (year, month) => {
        const items = get().items
        if (!Array.isArray(items)) return []
        return items.filter((item) => {
          const itemDate = new Date(item.date)
          return itemDate.getFullYear() === year && itemDate.getMonth() + 1 === month
        })
      },
      clearAllItems: () => {
        set({ items: [] })
      },
    }),
    {
      name: 'finance',
      storage: createAsyncStorage(),
    }
  )
)

export const initializeFinance = async (): Promise<void> => {
  try {
    // Rehydrate from persisted storage
    await useFinanceStore.persist.rehydrate()
    console.log('✅ Finance rehydrated from storage')
  } catch (error) {
    console.error('❌ Failed to initialize finance:', error)
  }
}
